"use client";

import { useEffect, useRef } from "react";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import TimelineItem from "./TimelineItem";
import { HowItWorksStep } from "@/types/data";
import styles from "./TimelineClient.module.css";

interface TimelineClientProps {
  steps: HowItWorksStep[];
}

export default function TimelineClient({ steps }: TimelineClientProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const LINE_ANIMATION_SPEED = 6;
  const LINE_ANIMATION_DURATION = 5;
  const LINE_EASE = "power2.out";

  const DOT_APPEARANCE_FACTOR = 0.3;
  const SPECIAL_DOT_FACTOR = 0.7;

  useEffect(() => {
    if (!timelineRef.current || !lineRef.current) return;

    const timeline = timelineRef.current;
    const line = lineRef.current;
    const items = itemsRef.current.filter(Boolean);

    const ctx = gsap.context(() => {
      gsap.set(line, { scaleY: 0, transformOrigin: "top" });
      gsap.set(items, { opacity: 0, y: 30 });

      const lineTimeline = gsap.timeline({
        scrollTrigger: {
          id: "timeline-line-animation",
          trigger: timeline,
          start: "top 90%",
          end: "bottom 15%",
          scrub: LINE_ANIMATION_SPEED,
          invalidateOnRefresh: true,
          refreshPriority: -1,
          scroller: window,
          markers: false,
          fastScrollEnd: false,
          onUpdate: (self) => {
            const progress = self.progress;
            const intensity = progress * 0.5 + 0.3;
            gsap.set(line, {
              boxShadow: `0 0 ${
                10 + progress * 20
              }px rgba(83, 120, 188, ${intensity})`,
            });

            items.forEach((item, index) => {
              if (!item) return;
              const dot = item.querySelector(".timeline-dot");
              if (dot) {
                const dotProgress = index / items.length;
                let shouldShow =
                  progress >= dotProgress * DOT_APPEARANCE_FACTOR;

                if (index === 2) {
                  shouldShow = progress >= dotProgress * SPECIAL_DOT_FACTOR;
                } else if (index === 3) {
                  shouldShow = progress >= dotProgress * SPECIAL_DOT_FACTOR;
                }

                if (shouldShow && gsap.getProperty(dot, "opacity") === 0) {
                  gsap.to(dot, {
                    scale: 1,
                    opacity: 1,
                    borderColor: "#5378BC",
                    boxShadow: "0 0 15px rgba(83, 120, 188, 0.6)",
                    duration: 0.5,
                    ease: "back.out(1.7)",
                  });
                }
              }
            });
          },
        },
      });

      lineTimeline.to(line, {
        scaleY: 1,
        duration: LINE_ANIMATION_DURATION,

        ease: LINE_EASE,
      });

      items.forEach((item, index) => {
        if (!item) return;

        const dot = item.querySelector(".timeline-dot");
        const content = item.querySelector(".timeline-content");

        if (dot)
          gsap.set(dot, { scale: 0, borderColor: "#e5e7eb", opacity: 0 });
        if (content) gsap.set(content, { opacity: 0, y: 30 });

        gsap.set(item, { opacity: 0, y: 50 });

        if (gsap.getProperty(item, "opacity") === 0) {
          gsap.to(item, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
          });

          if (content) {
            gsap.to(content, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power3.out",
            });
          }
        }

        let startPoint = "top 90%";
        let endPoint = "bottom 15%";

        if (index === 1) {
          startPoint = "top 95%";
          endPoint = "bottom 70%";
        } else if (index === 2) {
          startPoint = "top 95%";
          endPoint = "bottom 90%";
        }

        ScrollTrigger.create({
          id: `timeline-item-${index}`,
          trigger: item,
          start: startPoint,
          end: endPoint,
          scroller: window,
          refreshPriority: -1,
          onEnter: () => {

            item.classList.add("gsap-animated");

            const mainTimeline = gsap.timeline();

            mainTimeline.to(item, {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power3.out",
            });

            if (content) {
              const stepText = content.querySelector(".timeline-step");
              const title = content.querySelector(".timeline-title");
              const description = content.querySelector(
                ".timeline-description"
              );

              if (stepText) {
                mainTimeline.to(
                  stepText,
                  {
                    opacity: 1,
                    y: 0,
                    duration: 0,
                    ease: "power3.out",
                  },
                  "-=0.6"
                );
              }

              if (title) {
                mainTimeline.to(
                  title,
                  {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: "power3.out",
                  },
                  "-=0.4"
                );
              }

              if (description) {
                mainTimeline.to(
                  description,
                  {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: "power3.out",
                  },
                  "-=0.2"
                );
              }
            }
          },
          onEnterBack: () => {
            gsap.set(item, { opacity: 1, y: 0 });
            if (content) gsap.set(content, { opacity: 1, y: 0 });
          },
        });
      });
    }, timeline);

    return () => {
      ctx.revert();
    };
  }, [steps]);

  return (
    <div ref={timelineRef} className={styles["timeline-container"]}>
      {}
      <div ref={lineRef} className={styles["timeline-line"]} />

      {}
      {steps.map((step: HowItWorksStep, index: number) => (
        <div
          key={index}
          ref={(el) => {
            itemsRef.current[index] = el;
          }}
          className={styles["timeline-item"]}
        >
          <TimelineItem {...step} />
        </div>
      ))}
    </div>
  );
}
